import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, RotateCw, Sparkles, X, Trash2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

async function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsDataURL(file);
    });
}

// Downscale a data-URL client-side to keep payload well under 2 MB before it hits the API.
async function shrinkDataUrl(dataUrl, maxSide = 1200, quality = 0.9) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            const side = Math.max(width, height);
            const scale = side > maxSide ? maxSide / side : 1;
            width = Math.round(width * scale);
            height = Math.round(height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

export default function PassportPhotoUploader({ open, onClose, employeeId, onSaved, currentPhotoUrl, onPick, initialMode = "upload" }) {
    const [mode, setMode] = useState("upload"); // upload | camera
    const [preview, setPreview] = useState(null); // data URL
    const [refine, setRefine] = useState(true);
    const [saving, setSaving] = useState(false);
    const [streamActive, setStreamActive] = useState(false);
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    // Cleanup camera stream on close / unmount
    useEffect(() => {
        return () => stopCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!open) {
            stopCamera();
            setPreview(null);
            setMode("upload");
        } else if (initialMode === "camera") {
            setMode("camera");
            startCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setStreamActive(false);
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setStreamActive(true);
        } catch (err) {
            toast.error("Could not access camera. Check browser permissions.");
            setMode("upload");
        }
    };

    const capture = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const size = Math.min(video.videoWidth, video.videoHeight);
        const sx = (video.videoWidth - size) / 2;
        const sy = (video.videoHeight - size) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = 800;
        canvas.height = 800;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, sx, sy, size, size, 0, 0, 800, 800);
        setPreview(canvas.toDataURL("image/jpeg", 0.9));
        stopCamera();
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file (JPG or PNG)");
            return;
        }
        if (file.size > MAX_BYTES * 2) {
            // client-side shrink can bring anything under 2 MB up to ~10-15 MB source
            toast.error("Image is too large (max 4 MB source)");
            return;
        }
        const raw = await fileToDataUrl(file);
        const shrunk = await shrinkDataUrl(raw);
        setPreview(shrunk);
    };

    const save = async () => {
        if (!preview) return;
        setSaving(true);
        try {
            // Ensure payload is under 2 MB before send
            let payload = preview;
            if (payload.length > MAX_BYTES * 1.35) {
                payload = await shrinkDataUrl(payload, 1000, 0.85);
            }
            if (onPick) {
                // New employee (no record yet): hand the photo back to the form
                onPick(payload);
                onClose?.();
                return;
            }
            const { data } = await api.post(`/employees/${employeeId}/passport-photo`, {
                image_data_url: payload,
                refine,
            });
            toast.success(data.ai_refined ? "Photo refined and saved" : "Photo saved");
            onSaved?.(data);
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not save photo");
        } finally {
            setSaving(false);
        }
    };

    const removePhoto = async () => {
        setSaving(true);
        try {
            await api.delete(`/employees/${employeeId}/passport-photo`);
            toast.success("Photo removed");
            onSaved?.({ removed: true });
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not remove photo");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="passport-uploader">
            <div className="bg-white w-full max-w-2xl shadow-xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-bold text-lg text-ink">Passport photo</h3>
                        <p className="text-xs text-ink/50 mt-0.5">Auto-cropped to a square · Max 2 MB</p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-ink/5" data-testid="passport-close">
                        <X className="w-5 h-5 text-ink/60" />
                    </button>
                </div>

                <div className="px-6 py-4 flex items-center gap-2 border-b border-ink/10">
                    <button
                        onClick={() => { setPreview(null); stopCamera(); setMode("upload"); }}
                        className={`px-4 py-2 text-sm border ${mode === "upload" ? "bg-klein text-white border-klein" : "border-ink/20 text-ink hover:bg-ink/5"}`}
                        data-testid="passport-mode-upload"
                    >
                        <Upload className="w-4 h-4 inline-block mr-1" /> Upload file
                    </button>
                    <button
                        onClick={() => { setPreview(null); setMode("camera"); startCamera(); }}
                        className={`px-4 py-2 text-sm border ${mode === "camera" ? "bg-klein text-white border-klein" : "border-ink/20 text-ink hover:bg-ink/5"}`}
                        data-testid="passport-mode-camera"
                    >
                        <Camera className="w-4 h-4 inline-block mr-1" /> Take photo
                    </button>
                </div>

                <div className="p-6">
                    {mode === "upload" && !preview && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-ink/20 aspect-square max-w-sm mx-auto flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-klein hover:bg-klein/5"
                            data-testid="passport-upload-drop"
                        >
                            <Upload className="w-10 h-10 text-ink/40" />
                            <div className="text-sm text-ink/60">Click to choose an image</div>
                            <div className="text-xs text-ink/40">JPG or PNG · up to ~4 MB source</div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                                data-testid="passport-upload-input"
                            />
                        </div>
                    )}

                    {mode === "camera" && !preview && (
                        <div className="max-w-sm mx-auto">
                            <div className="aspect-square bg-black overflow-hidden">
                                <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                            </div>
                            <button
                                onClick={capture}
                                disabled={!streamActive}
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-klein text-white py-3 text-sm hover:bg-klein/90 disabled:opacity-50"
                                data-testid="passport-capture"
                            >
                                <Camera className="w-4 h-4" /> Capture
                            </button>
                        </div>
                    )}

                    {preview && (
                        <div className="max-w-sm mx-auto">
                            <div className="aspect-square bg-ink/5 overflow-hidden">
                                <img src={preview} alt="preview" className="w-full h-full object-cover" data-testid="passport-preview" />
                            </div>
                            <label className="flex items-center gap-2 mt-4 text-sm text-ink cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={refine}
                                    onChange={(e) => setRefine(e.target.checked)}
                                    className="w-4 h-4"
                                    data-testid="passport-refine-toggle"
                                />
                                <Sparkles className="w-4 h-4 text-klein" />
                                <span>Refine background to clean white with AI</span>
                            </label>
                            <div className="mt-4 flex gap-2">
                                <button
                                    onClick={() => { setPreview(null); if (mode === "camera") startCamera(); }}
                                    className="flex-1 inline-flex items-center justify-center gap-2 border border-ink/20 py-2.5 text-sm hover:bg-ink/5"
                                    data-testid="passport-retake"
                                >
                                    <RotateCw className="w-4 h-4" /> Retake
                                </button>
                                <button
                                    onClick={save}
                                    disabled={saving}
                                    className="flex-[2] inline-flex items-center justify-center gap-2 bg-klein text-white py-2.5 text-sm hover:bg-klein/90 disabled:opacity-50"
                                    data-testid="passport-save"
                                >
                                    <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save photo"}
                                </button>
                            </div>
                        </div>
                    )}

                    {currentPhotoUrl && !preview && (
                        <div className="mt-6 pt-6 border-t border-ink/10 flex justify-center">
                            <button
                                onClick={removePhoto}
                                disabled={saving}
                                className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm"
                                data-testid="passport-delete"
                            >
                                <Trash2 className="w-4 h-4" /> Remove current photo
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
